import { CheckIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/shared/lib'
import { AnimatedIcon } from '@/shared/ui/animated-icon'
import { IconButton } from '@/shared/ui/icon-button'
import { CopyIcon } from '@/shared/ui/icons/copy'

type CodeBlockProps = {
  /** The file the snippet belongs in — `index.html`, `script.js`. */
  filename?: string
  children: string
  className?: string
}

/**
 * A snippet the reader is meant to copy, not to read closely.
 *
 * The line never wraps: a broken URL or script tag cannot be copied by eye, so
 * the block scrolls inside itself instead. Copying is a button rather than a
 * selection, because a selection in a scrolling block is a fight.
 */
export function CodeBlock({ filename, children, className }: CodeBlockProps) {
  const { t } = useTranslation('common')
  const [isCopied, setIsCopied] = useState(false)

  const handleCopy = () => {
    void navigator.clipboard
      .writeText(children)
      .then(() => {
        setIsCopied(true)
        window.setTimeout(() => {
          setIsCopied(false)
        }, 1600)
      })
      /* Clipboard access can be denied — an insecure origin, a permission
       * policy. The button then simply never confirms, which is the honest
       * outcome: nothing was copied. */
      .catch(() => {
        setIsCopied(false)
      })
  }

  return (
    <div
      data-slot="code-block"
      className={cn('overflow-hidden rounded-lg border border-border bg-surface-sunken', className)}
    >
      <div className="flex h-control-md items-center justify-between gap-3 border-b border-border-hairline pr-1.5 pl-3">
        <span className="truncate font-mono text-caption text-muted-foreground">
          {filename ?? 'snippet'}
        </span>
        <IconButton
          label={isCopied ? t('ui.copied') : t('actions.copy')}
          size="sm"
          variant="ghost"
          onClick={handleCopy}
        >
          {isCopied ? <CheckIcon className="text-success-text" /> : <AnimatedIcon icon={CopyIcon} />}
        </IconButton>
      </div>

      <pre className="overflow-x-auto px-3 py-3 font-mono text-caption leading-text text-fg-body">
        {children}
      </pre>
    </div>
  )
}
